import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import { CreativeContext, Property_ID } from '@sites/data-hmm/hmm-incubator';
import { contextEffectActions } from './context.actions';
import { produce } from 'immer';

export const ContextKey = 'context';

export interface ContextEntityState extends EntityState<CreativeContext> {
  error?: Error;
  loading: boolean;
  loadingMore: boolean;
  currentPage: number;
  pages: Record<number, CreativeContext[]>;
  pageTokens: Record<number, string>;
  searchTerm?: string;
  creativeIds?: string[];
  totalCount: number;
  availableProperties: Property_ID[];
  availableMediaTypes: string[];
  availableFormats: string[];
}

export const contextAdapter: EntityAdapter<CreativeContext> =
  createEntityAdapter<CreativeContext>();

export const initialContextState: ContextEntityState =
  contextAdapter.getInitialState({
    error: undefined,
    loading: false,
    loadingMore: false,
    currentPage: 0,
    pages: {},
    pageTokens: {},
    searchTerm: undefined,
    creativeIds: undefined,
    totalCount: 0,
    availableProperties: [],
    availableMediaTypes: [],
    availableFormats: [],
  });

export const contextReducer = createReducer(
  initialContextState,
  on(contextEffectActions.listContext, (state): ContextEntityState => {
    return contextAdapter.removeAll({
      ...state,
      loading: true,
      error: undefined,
    });
  }),
  on(
    contextEffectActions.listContextSuccess,
    (state, { contexts }): ContextEntityState => {
      return contextAdapter.upsertMany(contexts, {
        ...state,
        loading: false,
      });
    }
  ),
  on(
    contextEffectActions.listContextFailure,
    (state, { error }): ContextEntityState =>
      produce(state, (draft) => {
        draft.error = error;
        draft.loading = false;
      })
  ),

  on(
    contextEffectActions.searchContext,
    (state, { term, creativeIds }): ContextEntityState => ({
      ...initialContextState,
      loading: true,
      searchTerm: term,
      creativeIds,
      // Preserve facets during loading so the filter tree doesn't collapse.
      availableProperties: state.availableProperties,
      availableMediaTypes: state.availableMediaTypes,
      availableFormats: state.availableFormats,
    })
  ),
  on(
    contextEffectActions.searchContextSuccess,
    (
      state,
      {
        contexts,
        nextPageToken,
        totalCount,
        availableProperties,
        availableMediaTypes,
        availableFormats,
      }
    ): ContextEntityState => ({
      ...state,
      loading: false,
      currentPage: 0,
      pages: { 0: contexts },
      pageTokens: nextPageToken ? { 0: nextPageToken } : {},
      totalCount,
      availableProperties: availableProperties ?? [],
      availableMediaTypes: availableMediaTypes ?? [],
      availableFormats: availableFormats ?? [],
    })
  ),
  on(
    contextEffectActions.searchContextFailure,
    (state, { error }): ContextEntityState => ({
      ...state,
      error,
      loading: false,
    })
  ),

  on(
    contextEffectActions.setCurrentPage,
    (state, { page }): ContextEntityState => ({
      ...state,
      currentPage: page,
    })
  ),
  on(
    contextEffectActions.fetchPage,
    (state): ContextEntityState => ({
      ...state,
      loadingMore: true,
    })
  ),
  on(
    contextEffectActions.fetchPageSuccess,
    (
      state,
      {
        page,
        contexts,
        nextPageToken,
        totalCount,
        availableProperties,
        availableMediaTypes,
        availableFormats,
      }
    ): ContextEntityState => ({
      ...state,
      loadingMore: false,
      currentPage: page,
      pages: { ...state.pages, [page]: contexts },
      pageTokens: nextPageToken
        ? { ...state.pageTokens, [page]: nextPageToken }
        : state.pageTokens,
      totalCount,
      availableProperties: availableProperties ?? state.availableProperties,
      availableMediaTypes: availableMediaTypes ?? state.availableMediaTypes,
      availableFormats: availableFormats ?? state.availableFormats,
    })
  ),
  on(
    contextEffectActions.fetchPageFailure,
    (state, { error }): ContextEntityState => ({
      ...state,
      error,
      loadingMore: false,
    })
  )
);
